import { useRef } from "react";
import {
  SidebarOverlay,
  InfiniteScrollGrid,
  Avatar,
  StatusLight,
  Button
} from "@etframework/ui.components";
import { useTranslation } from "react-i18next";
import useEvent from "../../hooks/useEvent";
import { ColumnDirective } from "@syncfusion/ej2-react-grids";
import { formatDate } from "../../utils/helpers";
import { DateFormats } from "../../types/DateFormats";
import colorPalette from "../../content/styles/scss/_palette.module.scss";
import ReactDOM from "react-dom";
import { forwardRef } from "react";
import { useAuth } from "oidc-react";
import { AppDataManager } from "../../utils/dataManager";

export const ImportLogOverlay = forwardRef(
  ({ id, type, taskTypeId = null, onReview, onNewImport }, overlayRef) => {
    const { userManager } = useAuth();

    const url =
      type === "tasks"
        ? "importbatches/tasks/grid"
        : `importbatches/tasktypes/${taskTypeId}/tasktemplates/grid`;

    let dataManager = new AppDataManager(
      url,
      userManager.signinSilent.bind(userManager)
    );

    let dataSource = type === "tasks" || taskTypeId ? dataManager : [];

    //Refs
    const tooltipRef = useRef();
    const gridRef = useRef();
    //Hooks
    const { t, i18n, ready } = useTranslation(["ImportLogOverlay", "Shared"], {
      useSuspense: false
    });

    const { Events, useRegisterCustomEvent } = useEvent();

    //Handlers
    const handleOverlayClose = () => {
      if (overlayRef.current) tooltipRef?.current?.close();
    };

    const fileNameTemplate = ({ FileName }) => {
      return <div className="file-name">{FileName}</div>;
    };

    const uploadedByTemplate = ({ User }) => {
      return (
        <div className="d-flex align-items-center">
          <Avatar content={User.FullName}></Avatar>
          <span className="ms-2">{User.FullName}</span>
        </div>
      );
    };

    const dateTemplate = ({ CreatedDate }) => {
      return (
        <div>
          {formatDate(
            CreatedDate,
            DateFormats.ShortDateTimeFormat.momentJsFormat,
            false
          )}
        </div>
      );
    };

    const tasksImportedTemplate = ({ Rows }) => {
      return <div>{Rows ?? "-"}</div>;
    };

    const statusTemplate = ({ Status, Errors }) => {
      let status = t("Pending");
      let color = "";
      switch (Status) {
        case 0:
          status = t("Queued");
          color = colorPalette.experttaxDarkGray;
          break;
        case 1:
          status = t("Processing");
          color = colorPalette.experttaxSemanticBlue;
          break;
        case 2:
          status = t("Failed");
          color = colorPalette.experttaxRedDark;
          break;
        case 3:
          status = t("Success");
          color = colorPalette.experttaxGreen;
          break;
      }

      return (
        <div className="d-flex justify-content-between">
          <div className={Status == 2 ? "mt-2" : ""}>
            <StatusLight color={color} text={status} />
          </div>

          {Status == 2 && (
            <div>
              <Button
                text={t("Review")}
                className="btn btn-default et-blue"
                onClick={() => onReview?.({ Errors })}
              />
            </div>
          )}
        </div>
      );
    };

    const refreshGrid = {
      id: id + "_refresh-grid-button",
      align: "Left",
      // type: "Button",
      visible: true,
      text: t("Shared:Refresh"),
      prefixIcon: "fa-regular fa-arrows-rotate",
      click: () => {
        refreshImportLogOverlay();
      },
      enabled: true
    };

    const toolbarItems = [refreshGrid];
    const searchSettings = {
      fields: ["FileName", "User.FullName", "CreatedDate"],
      operator: "contains",
      ignoreCase: true,
      align: "Right"
    };

    const refreshImportLogOverlay = () => {
      gridRef?.current?.getComponentRef()?.refresh();
    };

    useRegisterCustomEvent(
      Events.RefreshImportLogOverlay,
      refreshImportLogOverlay
    );

    if (!ready) return null;
    return ReactDOM.createPortal(
      <SidebarOverlay
        ref={overlayRef}
        id={id}
        className={"import-log-overlay"}
        width="75%"
        close={handleOverlayClose}
      >
        <div className="import-log-overlay px-3">
          <div className="d-flex justify-content-between py-3">
            <div className="header">{t("Header", { context: type })}</div>
            <div>
              <Button
                text={t("NewImport")}
                className="btn-tertiary new-import-btn"
                onClick={() => onNewImport?.()}
                iconCss="fa-light fa-plus"
              />
            </div>
          </div>

          <div className="import-log-overlay-content">
            <InfiniteScrollGrid
              ref={gridRef}
              id="import-log-grid"
              showHeader={false}
              toolbarItems={toolbarItems}
              searchSettings={searchSettings}
              dataSource={dataSource}
              allowResizing={true}
              allowReordering={true}
              enableRowSelection={false}
              allowFiltering={false}
              sortSettings={{
                columns: [{ field: "CreatedDate", direction: "Descending" }]
              }}
              autoHeight
              language={i18n.language}
            >
              <ColumnDirective
                field="FileName"
                headerText={t("FileName")}
                template={fileNameTemplate}
                allowReordering={false}
                width="290px"
                filter={{
                  type: "CheckBox"
                }}
              />
              <ColumnDirective
                field="User.FullName"
                headerText={t("UploadedBy")}
                template={uploadedByTemplate}
                allowReordering={false}
                filter={{
                  type: "CheckBox"
                }}
              />
              <ColumnDirective
                field="CreatedDate"
                headerText={t("Date")}
                template={dateTemplate}
                type="date"
              />
              <ColumnDirective
                field="Rows"
                headerText={t("Counter", { context: type })}
                template={tasksImportedTemplate}
                autoFit={true}
                filter={{
                  type: "CheckBox"
                }}
              />
              <ColumnDirective
                field="Status"
                headerText={t("Status")}
                template={statusTemplate}
                width="265px"
                filter={{
                  type: "CheckBox"
                }}
              />
            </InfiniteScrollGrid>
          </div>
        </div>
      </SidebarOverlay>,
      document.body
    );
  }
);

ImportLogOverlay.displayName = "Import Log Overlay";
